import {useCallback, useLayoutEffect, useRef, useState, type RefObject} from 'react';
import {flushSync} from 'react-dom';
import type {DockPosition} from '../dock/types';
import {createGenieAnimation, type GenieAnimationController, type GenieRect} from '../genie/genie';
import {captureGenieSnapshot} from '../genie/snapshot';
import type {MotionOrigin, WindowState} from '../types';

/* ============================================================================
   Window motion — a window never just appears or disappears
   ----------------------------------------------------------------------------
   Every state change a window can go through is a movement between two boxes:

     open      dock icon            → its box          scale
     minimise  its box              → its parking slot GENIE
     restore   whatever was clicked → its box          GENIE
     zoom      its previous box     → its new box      scale
     close     its box              → smaller, gone    scale

   The point is not decoration. A window that blinks out of existence makes the
   user look for it; a window that visibly pours into a slot beside the dock —
   and leaves a live thumbnail sitting there — has told them where it went and
   how to get it back.

   TWO ENGINES, AND WHY

   Minimise and restore run the genie: the window is rasterised once, and a
   full-screen overlay canvas warps that bitmap down a funnel into the slot
   (`../genie/genie.ts` — strips, two overlapping phases, all four dock edges).
   It is the one moment that deserves the cost, because it is the one moment the
   user needs to be told WHERE something went.

   Everything else — opening, zooming, closing — is a compositor transform on
   the live element: no rasterising, no overlay, and the real DOM stays
   interactive the whole time. Rasterising a window to open it would mean
   freezing the app for a frame in exchange for an effect nobody asked about.

   RULES THAT KEEP BOTH ENGINES HONEST

   · Transform flights animate only `transform` and `opacity`, the two
     properties the compositor owns. Animating width/height would reflow the
     whole app inside the window every frame — the kind of "smooth animation"
     that stutters on a table of 200 rows.

   · Every keyframe pins `transform-origin: 0 0`. The resting transform is a
     pure translate, which is origin-independent, so the animated and resting
     states agree, and an interrupted flight can hand its current matrix to the
     next one without a jump.

   · Every destination is measured from the DOM at flight time, and the minimise
     waits for that measurement to stop moving (a dock relaxing out of pointer
     magnification slides every slot under it). Nothing here assumes where the
     dock is, so moving it, reordering it or hiding it needs no change — and a
     destination that cannot be measured degrades to a shrink in place.

   · The genie is an enhancement, never a dependency: if the snapshot fails, or
     the slot cannot be found, the same minimise still happens as a transform
     flight. There is no path where a window simply vanishes.

   · `prefers-reduced-motion: reduce` turns all of it off, in both engines.
   ========================================================================== */

const OPEN_MS = 260;
const CLOSE_MS = 160;
const MINIMIZE_MS = 300;
const RESTORE_MS = 280;
const ZOOM_MS = 240;

/** Arrivals decelerate hard: the window is fastest when it leaves the dock. */
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
/** Departures accelerate: it is slowest while the user can still read it. */
const EASE_IN = 'cubic-bezier(0.55, 0, 0.85, 0.4)';
/** A zoom has no origin and no destination, so it eases at both ends. */
const EASE_ZOOM = 'cubic-bezier(0.32, 0.72, 0, 1)';

/** How many frames a minimise will wait for its destination to hold still. */
const AIM_FRAMES = 5;
/** How long a pre-captured bitmap may be reused before it is assumed stale. */
const WARM_SNAPSHOT_TTL_MS = 4000;

type Phase = 'idle' | 'opening' | 'minimizing' | 'restoring' | 'zooming' | 'closing';

interface FadeSpec {
  from: number;
  to: number;
  /** Fractions of the flight the fade occupies, e.g. `[0.55, 1]` = last 45%. */
  span: [number, number];
}

interface WindowBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

function reduceMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

function slotEl(id: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-minimized-id="${CSS.escape(id)}"]`);
}

function measure(el: Element | null): MotionOrigin | null {
  const rect = el?.getBoundingClientRect();
  return rect && rect.width >= 1 && rect.height >= 1 ? rect : null;
}

/** The parking slot beside the dock, once the window has been minimised. */
function slotBox(id: string): MotionOrigin | null {
  return measure(slotEl(id));
}

/** The app's own dock icon — where a window is launched from. */
function iconBox(appId: string): MotionOrigin | null {
  return measure(document.querySelector(`[data-dock-id="${CSS.escape(appId)}"]`));
}

function toGenieRect(box: MotionOrigin): GenieRect {
  return {height: box.height, left: box.left, top: box.top, width: box.width};
}

/** Where the window sits when nothing is animating it. */
function restTransform(state: WindowState): string {
  return `translate3d(${state.x}px, ${state.y}px, 0) scale(1, 1)`;
}

/**
 * The transform that folds the window onto `target`.
 *
 * The caller must have cancelled any running animation first: this reads the
 * element's box to locate the origin of its coordinate space (the window is
 * `left: 0; top: 0` inside the desktop layer and positioned purely by
 * `translate3d`, so `rect.left - state.x` is where it would sit untransformed),
 * and a filled animation would poison that measurement.
 *
 * The scale is uniform. Squashing the window into the slot's exact rectangle
 * would shear its content on the way down; keeping the aspect ratio and
 * centring the result on the slot reads as the window folding into it.
 */
function foldTransform(el: HTMLElement, state: WindowState, target: MotionOrigin | null): string {
  const rect = el.getBoundingClientRect();

  // Nowhere to fly to — no dock, no slot, or nothing measurable yet. Shrinking
  // in place still says "this went away", it just does not say where.
  if (!target || rect.width < 1) {
    return `translate3d(${state.x + state.width * 0.06}px, ${
      state.y + state.height * 0.06
    }px, 0) scale(0.88, 0.88)`;
  }

  const originX = rect.left - state.x;
  const originY = rect.top - state.y;
  const scale = Math.min(target.width / rect.width, 1);
  const x = target.left + target.width / 2 - (rect.width * scale) / 2 - originX;
  const y = target.top + target.height / 2 - (rect.height * scale) / 2 - originY;
  return `translate3d(${x}px, ${y}px, 0) scale(${scale}, ${scale})`;
}

/**
 * The transform that maps the window's previous box onto its current one, so a
 * zoom can start from where the window used to be while the DOM already holds
 * its new size — the standard FLIP inversion, expressed as scale so no layout
 * happens per frame.
 */
function boxTransform(box: WindowBox, state: WindowState): string {
  return `translate3d(${box.x}px, ${box.y}px, 0) scale(${box.width / state.width}, ${
    box.height / state.height
  })`;
}

/**
 * Fade rides its own clock rather than a keyframe offset on the transform
 * animation: a window should stay solid for most of the flight and only vanish
 * as it reaches the dock, and one easing curve cannot say both "move like this"
 * and "fade like that".
 */
function animate(
  el: HTMLElement,
  from: string,
  to: string,
  options: {duration: number; easing: string; fade?: FadeSpec},
): Animation[] {
  const lead = el.animate(
    [
      {transform: from, transformOrigin: '0 0'},
      {transform: to, transformOrigin: '0 0'},
    ],
    {duration: options.duration, easing: options.easing, fill: 'both'},
  );

  if (!options.fade) return [lead];
  const {from: opacityFrom, to: opacityTo, span} = options.fade;
  const follow = el.animate([{opacity: opacityFrom}, {opacity: opacityTo}], {
    delay: Math.round(options.duration * span[0]),
    duration: Math.round(options.duration * (span[1] - span[0])),
    easing: 'linear',
    fill: 'both',
  });
  return [lead, follow];
}

interface WarmSnapshot {
  at: number;
  box: WindowBox;
  canvas: HTMLCanvasElement;
  rect: GenieRect;
}

export interface WindowMotion {
  /** True once a minimised window has finished travelling into the dock. */
  parked: boolean;
  /** True while the genie overlay owns the window's pixels: laid out, unpainted. */
  veiled: boolean;
  /**
   * Rasterise ahead of time. Wire this to the minimise button's pointer-enter:
   * see the note on `prewarm` in the hook for why that is the right moment.
   */
  prewarm: () => void;
  /** Play the close flight, then run `onClosed`. */
  requestClose: () => void;
}

export function useWindowMotion(
  elRef: RefObject<HTMLElement | null>,
  state: WindowState,
  {
    dockPosition,
    onClosed,
    releaseSlot,
    takeRestoreOrigin,
  }: {
    /** Which edge the dock lives on — the genie funnels toward it. */
    dockPosition: DockPosition;
    onClosed: () => void;
    /** Lets the parking slot unmount, once the window is fully out of it. */
    releaseSlot: (id: string) => void;
    /**
     * The box the restore should grow out of, as measured by whatever the user
     * actually clicked — the parking slot, or the app's dock icon. It has to be
     * measured at click time and handed over, because a magnified dock item is
     * not where it was drawn a frame ago.
     */
    takeRestoreOrigin: (id: string) => MotionOrigin | null;
  },
): WindowMotion {
  // `parked` is what hides the window, not `state.minimized`: the window has to
  // stay rendered for the whole flight into the dock, and only stops taking up
  // pixels once it has arrived. `veiled` is the softer version used while the
  // genie overlay holds the pixels — the element keeps its layout box so it can
  // still be measured.
  const [parked, setParked] = useState(state.minimized);
  const [veiled, setVeiled] = useState(false);

  const animsRef = useRef<Animation[]>([]);
  const frameRef = useRef<number | null>(null);
  const genieRef = useRef<GenieAnimationController | null>(null);
  // The bitmap the window was folded from, kept so the way back can unfold the
  // same pixels. Cleared as soon as the window is out again.
  const snapshotRef = useRef<HTMLCanvasElement | null>(null);
  // A bitmap taken before the click, if the pointer gave us the warning.
  const warmRef = useRef<WarmSnapshot | null>(null);
  const capturingRef = useRef(false);
  const phaseRef = useRef<Phase>('idle');
  const wantRestoreRef = useRef(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;
  const takeOriginRef = useRef(takeRestoreOrigin);
  takeOriginRef.current = takeRestoreOrigin;
  const releaseSlotRef = useRef(releaseSlot);
  releaseSlotRef.current = releaseSlot;
  const dockPositionRef = useRef(dockPosition);
  dockPositionRef.current = dockPosition;

  // Geometry as of the previous commit, so a zoom knows where it came from.
  const boxRef = useRef<WindowBox>({
    height: state.height,
    width: state.width,
    x: state.x,
    y: state.y,
  });
  const maximizedRef = useRef(state.maximized);

  const stop = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    genieRef.current?.cancel();
    genieRef.current = null;
    animsRef.current.forEach((anim) => anim.cancel());
    animsRef.current = [];
    phaseRef.current = 'idle';
  }, []);

  /**
   * Start a transform phase. `release` cancels the fill once the flight lands,
   * handing the element back to its inline transform — which is the same
   * position, so there is no jump. A minimise deliberately keeps its fill: the
   * window must stay folded and invisible in the frame between "arrived" and
   * "React hid it".
   */
  const start = useCallback(
    (phase: Phase, anims: Animation[], onLanded: (() => void) | null, release: boolean) => {
      phaseRef.current = phase;
      animsRef.current = anims;
      const lead = anims[0];
      if (!lead) return;
      lead.finished.then(
        () => {
          // A later phase already took the element over; it owns the outcome.
          if (animsRef.current !== anims) return;
          if (release) {
            anims.forEach((anim) => anim.cancel());
            animsRef.current = [];
          }
          phaseRef.current = 'idle';
          onLanded?.();
        },
        () => {
          /* cancelled — the interrupting phase is responsible from here */
        },
      );
    },
    [],
  );

  /**
   * Aim, then fire — but only once the target has stopped moving.
   *
   * The slot is sized on the frame it mounts, yet the dock around it can still
   * be in motion: relaxing out of pointer magnification shifts every item, and
   * a destination measured mid-relax is a destination the window lands beside.
   * So compare consecutive frames and wait for them to agree, with a hard cap —
   * a dock that never settles must not hold a window hostage.
   */
  const aimAtSlot = useCallback(
    (id: string) =>
      new Promise<MotionOrigin | null>((resolve) => {
        const step = (previous: MotionOrigin | null, attempt: number) => {
          frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null;
            const box = slotBox(id);
            const settled =
              box !== null &&
              previous !== null &&
              Math.abs(box.left - previous.left) < 0.5 &&
              Math.abs(box.top - previous.top) < 0.5;
            if (settled || attempt >= AIM_FRAMES) {
              resolve(box);
              return;
            }
            step(box, attempt + 1);
          });
        };
        step(null, 0);
      }),
    [],
  );

  /* ------------------------------------------------------------ snapshots  */

  /** A pre-captured bitmap is only usable while it is still the truth. */
  const warmSnapshot = useCallback((current: WindowState) => {
    const warm = warmRef.current;
    if (!warm) return null;
    const {box} = warm;
    const sameBox =
      box.x === current.x &&
      box.y === current.y &&
      box.width === current.width &&
      box.height === current.height;
    if (!sameBox || performance.now() - warm.at > WARM_SNAPSHOT_TTL_MS) {
      warmRef.current = null;
      return null;
    }
    return warm;
  }, []);

  /**
   * Rasterising a window costs a fifth of a second of main thread — 359 nodes of
   * inlined computed style — and no option on the capture makes that cheaper.
   * What DOES make it free is spending it at a different moment: the pointer
   * entering the minimise button is a promise that a click is coming, and the
   * cursor is standing still on a 40px target while it happens, so a long task
   * there costs nothing anyone can see. By the time the click lands the bitmap
   * is ready and the genie starts on the next frame.
   *
   * If the warning never comes (keyboard, a script, a very fast pointer) the
   * minimise captures on the spot — same picture, just later.
   */
  const prewarm = useCallback(() => {
    const el = elRef.current;
    const current = stateRef.current;
    if (!el || current.minimized || capturingRef.current || reduceMotion()) return;
    if (warmSnapshot(current)) return;
    capturingRef.current = true;
    void captureGenieSnapshot(el).then(
      (snapshot) => {
        capturingRef.current = false;
        if (!snapshot) return;
        const now = stateRef.current;
        warmRef.current = {
          at: performance.now(),
          box: {height: now.height, width: now.width, x: now.x, y: now.y},
          canvas: snapshot.canvas,
          rect: snapshot.rect,
        };
      },
      () => {
        capturingRef.current = false;
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warmSnapshot]);

  /* --------------------------------------------------------------- opening  */

  useLayoutEffect(() => {
    const el = elRef.current;
    const current = stateRef.current;
    if (!el || current.minimized || reduceMotion()) return undefined;
    start(
      'opening',
      animate(el, foldTransform(el, current, iconBox(current.appId)), restTransform(current), {
        duration: OPEN_MS,
        easing: EASE_OUT,
        fade: {from: 0, to: 1, span: [0, 0.5]},
      }),
      null,
      true,
    );
    // Mount only. Under StrictMode this runs, is cancelled, and runs again —
    // which is exactly right: the second pass replays the flight from scratch
    // instead of leaving a half-cancelled one on screen.
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------------------------------- minimising / restoring */

  /** The transform flight, used when the genie has nothing to work with. */
  const foldWithTransform = useCallback(
    (el: HTMLElement, current: WindowState, target: MotionOrigin | null) => {
      slotEl(current.id)?.removeAttribute('data-flying');
      start(
        'minimizing',
        animate(el, restTransform(current), foldTransform(el, current, target), {
          duration: MINIMIZE_MS,
          easing: EASE_IN,
          fade: {from: 1, to: 0, span: [0.6, 1]},
        }),
        () => {
          if (stateRef.current.minimized) setParked(true);
        },
        false,
      );
    },
    [start],
  );

  const runMinimize = useCallback(
    async (el: HTMLElement, current: WindowState) => {
      // The slot mounted in this same commit. Hide it now — synchronously,
      // before this commit paints — so the thumbnail cannot appear before the
      // window has even left. It is `data-flying` rather than a default
      // `opacity: 0` so that a failure anywhere below leaves the thumbnail
      // VISIBLE: a lost window is a bug, a thumbnail that appears early is not.
      const slot = slotEl(current.id);
      slot?.setAttribute('data-flying', '');

      // Use the bitmap the pointer bought us, or rasterise now — while the
      // window is still on screen and still square.
      const warm = warmSnapshot(current);
      warmRef.current = null;
      const snapshot = warm ?? (await captureGenieSnapshot(el));
      if (phaseRef.current !== 'minimizing') return;

      const target = await aimAtSlot(current.id);
      if (phaseRef.current !== 'minimizing') return;

      const controller =
        snapshot && target
          ? createGenieAnimation({
              direction: 'minimize',
              originalRect: snapshot.rect,
              ownerDocument: document,
              position: dockPositionRef.current,
              sourceCanvas: snapshot.canvas,
              targetRect: toGenieRect(target),
            })
          : null;

      if (!controller || !snapshot || !target) {
        foldWithTransform(el, current, target);
        return;
      }

      const landing = toGenieRect(target);
      snapshotRef.current = snapshot.canvas;
      genieRef.current = controller;

      // The overlay already holds frame 0 — an exact copy of the window, in the
      // window's own place — so hiding the real element underneath it is
      // invisible. `flushSync` because that has to be true of THIS frame, not
      // of whichever frame React would otherwise have chosen.
      flushSync(() => setParked(true));

      await controller.play({
        beforeFinish: async ({drawFinalFrame}) => {
          // Paint the bitmap unwarped in the slot and reveal the real thumbnail
          // underneath it, while the overlay is still up: the hand-off happens
          // behind a picture of itself, so there is no frame with a gap in it.
          drawFinalFrame(landing);
          slot?.removeAttribute('data-flying');
          await nextFrame();
        },
      });

      genieRef.current = null;
      if (phaseRef.current === 'minimizing') phaseRef.current = 'idle';
    },
    [aimAtSlot, foldWithTransform, warmSnapshot],
  );

  const runRestore = useCallback(
    async (el: HTMLElement, current: WindowState, target: MotionOrigin | null) => {
      const sourceCanvas = snapshotRef.current;
      const slot = slotEl(current.id);

      // `veiled`, not `parked`: the element keeps its layout box, so this is the
      // real destination rect rather than a prediction of one.
      const originalRect = measure(el);

      const controller =
        sourceCanvas && target && originalRect
          ? createGenieAnimation({
              direction: 'restore',
              originalRect: toGenieRect(originalRect),
              ownerDocument: document,
              position: dockPositionRef.current,
              sourceCanvas,
              targetRect: toGenieRect(target),
            })
          : null;

      if (!controller) {
        // No bitmap to unfold — grow the live element out of the slot instead.
        setVeiled(false);
        releaseSlotRef.current(current.id);
        start(
          'restoring',
          animate(el, foldTransform(el, current, target), restTransform(current), {
            duration: RESTORE_MS,
            easing: EASE_OUT,
            fade: {from: 0, to: 1, span: [0, 0.45]},
          }),
          null,
          true,
        );
        return;
      }

      // Frame 0 is the fully-funnelled shape sitting exactly on the slot, so the
      // thumbnail can go now without anything blinking.
      slot?.setAttribute('data-flying', '');
      genieRef.current = controller;

      await controller.play({
        beforeFinish: async () => {
          flushSync(() => setVeiled(false));
          releaseSlotRef.current(current.id);
          await nextFrame();
        },
      });

      genieRef.current = null;
      snapshotRef.current = null;
      if (phaseRef.current === 'restoring') phaseRef.current = 'idle';
    },
    [start],
  );

  useLayoutEffect(() => {
    const el = elRef.current;
    if (!el) return;
    const current = stateRef.current;

    if (reduceMotion()) {
      setParked(current.minimized);
      setVeiled(false);
      if (!current.minimized) releaseSlotRef.current(current.id);
      return;
    }

    if (current.minimized) {
      if (parked || phaseRef.current === 'minimizing') return;
      stop();
      phaseRef.current = 'minimizing';
      void runMinimize(el, current);
      return;
    }

    // Coming back. The window must be laid out before it can be measured or
    // animated, so un-parking is its own pass; the flight starts on the next
    // one, still before paint, so nothing is ever shown in the wrong place.
    if (parked) {
      wantRestoreRef.current = true;
      setVeiled(true);
      setParked(false);
      return;
    }

    const interrupted = phaseRef.current === 'minimizing';
    if (!wantRestoreRef.current && !interrupted) return;
    // Was the window already hidden when the user changed their mind? If so this
    // is an ordinary restore, however far the minimise had got; only a window
    // that is still on screen can be turned round where it stands.
    const wasParked = wantRestoreRef.current;
    wantRestoreRef.current = false;

    // An interrupted minimise resumes from wherever it had got to, not from the
    // slot — otherwise the window jumps down to the dock before coming back.
    // That reading must happen BEFORE `stop()` (it is the live animated value)
    // and `foldTransform` must happen AFTER it: a parked window still carries
    // the filled minimise animation, so measuring it first would report the
    // folded box and produce a "from" that is already the destination.
    const turningRound = interrupted && !wasParked;
    const wasFlying = turningRound && animsRef.current.length > 0;
    const computed = wasFlying ? getComputedStyle(el) : null;
    const heldTransform = computed ? computed.transform : null;
    const fromOpacity = computed ? Number(computed.opacity) : 0;
    const origin = takeOriginRef.current(current.id) ?? slotBox(current.id) ?? iconBox(current.appId);
    stop();

    if (turningRound) {
      // The window never went away. Either a transform flight was carrying it —
      // and then the way back starts from exactly where that left it — or the
      // genie had not taken over yet and there is simply nothing to undo.
      setVeiled(false);
      releaseSlotRef.current(current.id);
      if (heldTransform) {
        start(
          'restoring',
          animate(el, heldTransform, restTransform(current), {
            duration: RESTORE_MS,
            easing: EASE_OUT,
            fade: {from: fromOpacity, to: 1, span: [0, 0.45]},
          }),
          null,
          true,
        );
      }
      return;
    }

    phaseRef.current = 'restoring';
    void runRestore(el, current, origin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.minimized, parked]);

  /* --------------------------------------------------------------- zooming  */

  useLayoutEffect(() => {
    const el = elRef.current;
    const current = stateRef.current;
    if (maximizedRef.current === current.maximized) return;
    maximizedRef.current = current.maximized;
    if (!el || current.minimized || parked || reduceMotion()) return;
    if (phaseRef.current !== 'idle') return;
    start(
      'zooming',
      animate(el, boxTransform(boxRef.current, current), restTransform(current), {
        duration: ZOOM_MS,
        easing: EASE_ZOOM,
      }),
      null,
      true,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.maximized]);

  // Runs on every commit, and declared last so the zoom effect above still sees
  // the previous commit's geometry. Dragging and resizing write here too, which
  // is the point: a zoom that follows a drag starts from where the window was
  // actually left, not from where it was opened.
  useLayoutEffect(() => {
    boxRef.current = {height: state.height, width: state.width, x: state.x, y: state.y};
  });

  /* --------------------------------------------------------------- closing  */

  const requestClose = useCallback(() => {
    const el = elRef.current;
    const current = stateRef.current;
    if (!el || parked || reduceMotion() || phaseRef.current === 'closing') {
      onClosedRef.current();
      return;
    }
    stop();
    start(
      'closing',
      animate(
        el,
        restTransform(current),
        `translate3d(${current.x + current.width * 0.04}px, ${
          current.y + current.height * 0.04
        }px, 0) scale(0.92, 0.92)`,
        {duration: CLOSE_MS, easing: EASE_IN, fade: {from: 1, to: 0, span: [0, 1]}},
      ),
      () => onClosedRef.current(),
      false,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parked, start, stop]);

  useLayoutEffect(() => {
    const id = stateRef.current.id;
    return () => {
      stop();
      // A window that is torn down mid-flight must not leave its slot behind.
      releaseSlotRef.current(id);
    };
  }, [stop]);

  return {parked, prewarm, requestClose, veiled};
}
